import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'

const STAGES = ['qualification', 'proposal', 'negotiation', 'closed_won'] as const
type Stage = typeof STAGES[number] | 'closed_lost'

interface StageProgressBarProps {
  stage: string
  compact?: boolean
}

function getStageIndex(stage: string): number {
  const normalized = stage.toLowerCase()
  if (normalized === 'closed_lost') return STAGES.indexOf('closed_won')
  const idx = STAGES.indexOf(normalized as typeof STAGES[number])
  return idx >= 0 ? idx : 0
}

function getStageLabel(stage: string): string {
  const labels: Record<string, string> = {
    qualification: 'Qualification',
    proposal: 'Proposal',
    negotiation: 'Negotiation',
    closed_won: 'Won',
    closed_lost: 'Lost',
  }
  return labels[stage.toLowerCase()] || stage
}

export function StageProgressBar({ stage, compact = false }: StageProgressBarProps) {
  const normalizedStage = stage.toLowerCase() as Stage
  const currentIndex = getStageIndex(normalizedStage)
  const isLost = normalizedStage === 'closed_lost'
  const isWon = normalizedStage === 'closed_won'

  if (compact) {
    return (
      <div className="flex items-center gap-1.5">
        {STAGES.map((s, index) => {
          const isCompleted = index < currentIndex
          const isCurrent = index === currentIndex
          const isLostStage = isCurrent && isLost
          const isWonStage = isCurrent && isWon

          return (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                isLostStage
                  ? 'bg-red-500'
                  : isWonStage
                  ? 'bg-emerald-500'
                  : isCompleted
                  ? 'bg-emerald-500'
                  : isCurrent
                  ? 'bg-blue-500'
                  : 'bg-gray-200'
              }`}
              title={getStageLabel(s === 'closed_won' && isLost ? 'closed_lost' : s)}
            />
          )
        })}
      </div>
    )
  }

  return (
    <nav aria-label="Progress">
      <ol className="flex items-center">
        {STAGES.map((s, index) => {
          const isCompleted = index < currentIndex
          const isCurrent = index === currentIndex
          const isLostStage = isCurrent && isLost
          const isWonStage = isCurrent && isWon
          const isLast = index === STAGES.length - 1
          const label = s === 'closed_won' && isLost ? 'closed_lost' : s

          return (
            <li key={s} className={`relative ${isLast ? '' : 'pr-8 sm:pr-12'}`}>
              {isCompleted ? (
                <>
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    {!isLast && (
                      <div className="h-0.5 w-full bg-emerald-500" />
                    )}
                  </div>
                  <div className="relative flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500">
                    <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" />
                  </div>
                </>
              ) : isCurrent ? (
                <>
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    {!isLast && (
                      <div className="h-0.5 w-full bg-gray-200" />
                    )}
                  </div>
                  <div
                    className={`relative flex h-7 w-7 items-center justify-center rounded-full border-2 ${
                      isLostStage
                        ? 'border-red-500 bg-red-500'
                        : isWonStage
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-blue-500 bg-white'
                    }`}
                    aria-current="step"
                  >
                    {isLostStage ? (
                      <XMarkIcon className="h-4 w-4 text-white" aria-hidden="true" />
                    ) : isWonStage ? (
                      <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" />
                    ) : (
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    {!isLast && (
                      <div className="h-0.5 w-full bg-gray-200" />
                    )}
                  </div>
                  <div className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                    <span className="h-2 w-2 rounded-full bg-transparent" />
                  </div>
                </>
              )}
              <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-gray-500">
                {getStageLabel(label)}
              </span>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// Compact inline version for tables
export function StageProgressInline({ stage }: { stage: string }) {
  return (
    <div className="w-24">
      <StageProgressBar stage={stage} compact />
    </div>
  )
}
